import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Store, KeyRound, Power, Copy, RefreshCcw, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreLoginsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [issuing, setIssuing] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);
  const [showPwd, setShowPwd] = useState(false);

  function load() {
    setLoading(true);
    const tok = localStorage.getItem('token');
    axios.get(`${API}/admin/store-logins`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error('Failed to load store logins'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function issueLogin(franchiseId) {
    if (!window.confirm(`Issue/Reset login for ${franchiseId}? A new temporary password will be generated.`)) return;
    setIssuing(franchiseId);
    const tok = localStorage.getItem('token');
    axios.post(`${API}/admin/store-logins/issue`, { franchise_id: franchiseId },
      { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => {
        setTempPwd({ franchise_id: r.data.franchise_id, store_name: r.data.store_name, password: r.data.temp_password });
        setShowPwd(true);
        load();
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to issue login'))
      .finally(() => setIssuing(null));
  }

  function deactivate(franchiseId) {
    if (!window.confirm(`Deactivate store login for ${franchiseId}? They won't be able to sign in until you re-issue a password.`)) return;
    const tok = localStorage.getItem('token');
    axios.post(`${API}/admin/store-logins/${encodeURIComponent(franchiseId)}/deactivate`, {},
      { headers: { Authorization: `Bearer ${tok}` } })
      .then(() => { toast.success('Deactivated'); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed'));
  }

  function copyPwd() {
    if (!tempPwd) return;
    navigator.clipboard.writeText(tempPwd.password);
    toast.success('Password copied');
  }

  const filtered = !q.trim() ? rows : rows.filter(r =>
    [r.franchise_id, r.name, r.email, r.phone].some(v =>
      (v || '').toString().toLowerCase().includes(q.toLowerCase()),
    ),
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Store className="w-5 h-5 text-emerald-700" /> Store / Franchise Logins
        </CardTitle>
        <p className="text-xs text-slate-500">
          Issue passwords so onboarded stores can log into the self-service portal at <code>/store/login</code>.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search franchise id / name / email"
                   value={q} onChange={(e) => setQ(e.target.value)} data-testid="store-logins-search" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="store-logins-refresh">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-slate-400">No onboarded stores match.</div>
        ) : (
          <div className="border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Franchise ID</th>
                  <th className="px-3 py-2 text-left font-semibold">Store</th>
                  <th className="px-3 py-2 text-left font-semibold">Segment</th>
                  <th className="px-3 py-2 text-left font-semibold">Email</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                  <th className="px-3 py-2 text-center font-semibold">Last login</th>
                  <th className="px-3 py-2 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`store-login-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono">{r.franchise_id || '—'}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-xs">{r.business_segment || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.email || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {r.store_login_active === false
                        ? <Badge className="bg-red-100 text-red-700">DEACTIVATED</Badge>
                        : r.store_login_issued_at
                        ? (r.force_password_reset
                            ? <Badge className="bg-amber-100 text-amber-800">PWD RESET PENDING</Badge>
                            : <Badge className="bg-emerald-100 text-emerald-700">ACTIVE</Badge>)
                        : <Badge variant="outline">NOT ISSUED</Badge>}
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-slate-500">{(r.last_login || '').split('T')[0] || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex gap-2 items-center justify-center flex-wrap">
                        {r.store_login_issued_at ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => issueLogin(r.franchise_id || r.id)}
                            disabled={issuing === (r.franchise_id || r.id)}
                            className="text-amber-700 border-amber-400 hover:bg-amber-50 h-8"
                            data-testid={`issue-login-${r.id}`}
                          >
                            <RefreshCcw className="w-3.5 h-3.5 mr-1" />
                            Reset Password
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => issueLogin(r.franchise_id || r.id)}
                            disabled={issuing === (r.franchise_id || r.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                            data-testid={`issue-login-${r.id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1" />
                            Issue Password
                          </Button>
                        )}
                        {r.store_login_issued_at && r.store_login_active !== false && (
                          <Button size="sm" variant="ghost" onClick={() => deactivate(r.franchise_id || r.id)}
                                  title="Deactivate" className="text-red-600 h-8"
                                  data-testid={`deactivate-login-${r.id}`}>
                            <Power className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Temp password reveal dialog */}
      <Dialog open={showPwd} onOpenChange={setShowPwd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Password Issued</DialogTitle>
          </DialogHeader>
          {tempPwd && (
            <div className="space-y-3">
              <p className="text-sm">Share these credentials with <b>{tempPwd.store_name}</b>:</p>
              <div className="bg-slate-100 rounded p-3 space-y-2 text-sm">
                <div><span className="text-slate-500">Franchise ID:</span> <span className="font-mono">{tempPwd.franchise_id}</span></div>
                <div className="flex items-center justify-between gap-2">
                  <span><span className="text-slate-500">Password:</span> <span className="font-mono text-base font-bold text-emerald-800">{tempPwd.password}</span></span>
                  <Button size="sm" variant="outline" onClick={copyPwd} data-testid="copy-temp-pwd">
                    <Copy className="w-3 h-3 mr-1" /> Copy
                  </Button>
                </div>
              </div>
              <p className="text-xs text-amber-700">
                The store will be forced to set a new password on first login. This temp password is shown only once.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
